import React, { useRef, useState, useEffect } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Camera, FileDown, Video, MapPin, Home, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import type { Hotspot, HotspotIcon } from '../types';

function Icon360FP({ size = 16, className = 'text-white' }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8" />
      <ellipse cx="12" cy="12" rx="4.5" ry="10" stroke="currentColor" strokeWidth="1.2" />
      <path d="M2.5 12h19" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4 7h16M4 17h16" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
    </svg>
  );
}

function HotspotIconComponent({ type, iconUrl }: { type?: HotspotIcon; iconUrl?: string | null }) {
  const t = type ?? 'camera';
  const props = { size: 16, className: 'text-white' };
  if (t === 'custom' && iconUrl) {
    return (
      <img src={iconUrl} alt="" className="w-4 h-4 object-contain" style={{ maxWidth: 16, maxHeight: 16 }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
    );
  }
  switch (t) {
    case 'pano360': return <Icon360FP {...props} />;
    case 'file': return <FileDown {...props} />;
    case 'video': return <Video {...props} />;
    case 'map-pin': return <MapPin {...props} />;
    case 'home': return <Home {...props} />;
    default: return <Camera {...props} />;
  }
}

interface ZoomableFloorPlanProps {
  imageUrl: string;
  hotspots: Hotspot[];
  onHotspotClick: (h: Hotspot) => void;
  onHotspotHover?: (h: Hotspot | null, anchor?: { x: number; y: number } | null) => void;
  selectedId?: string | null;
  hoveredId?: string | null;
  primaryColor?: string;
  /** Called when the floor plan image fails to load (e.g. 403). Viewer can show a fallback message. */
  onImageError?: () => void;
}

const ZoomableFloorPlan: React.FC<ZoomableFloorPlanProps> = ({
  imageUrl,
  hotspots,
  onHotspotClick,
  onHotspotHover,
  selectedId,
  hoveredId,
  primaryColor = '#FF3B30',
  onImageError,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 400, height: 400 });
  const floorplanHotspots = hotspots.filter((h) => h.x_pct != null && h.y_pct != null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setDimensions({ width: rect.width, height: rect.height });
      }
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 w-full h-full min-h-[280px]"
      style={{ overflow: 'hidden' }}
    >
      <TransformWrapper
        initialScale={1}
        minScale={0.5}
        maxScale={4}
        centerOnInit
        limitToBounds={false}
        doubleClick={{ mode: 'reset', excluded: ['button', 'planos-hotspot'] }}
        wheel={{ step: 0.08, excluded: ['button', 'planos-hotspot'] }}
        pinch={{ step: 5, excluded: ['button', 'planos-hotspot'] }}
        panning={{ disabled: false, velocityDisabled: true, activationKeys: [], excluded: ['planos-hotspot'], allowLeftClickPan: true }}
        style={{
          width: dimensions.width,
          height: dimensions.height,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            <div className="absolute bottom-4 right-4 z-10 flex flex-col gap-1 rounded-lg border border-white/20 bg-black/60 p-1 shadow-lg backdrop-blur-sm">
              <button type="button" onClick={(e) => { e.stopPropagation(); zoomIn(); }} className="planos-hotspot p-2 rounded-md text-white hover:bg-white/20 transition-colors" title="Zoom in" style={{ color: primaryColor }}>
                <ZoomIn size={20} />
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); zoomOut(); }} className="planos-hotspot p-2 rounded-md text-white hover:bg-white/20 transition-colors" title="Zoom out">
                <ZoomOut size={20} style={{ color: primaryColor }} />
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); resetTransform(); }} className="planos-hotspot p-2 rounded-md text-white hover:bg-white/20 transition-colors" title="Reset view">
                <RotateCcw size={18} style={{ color: primaryColor }} />
              </button>
            </div>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 rounded-full bg-black/50 text-xs text-white/80 pointer-events-none">
              Drag to pan · Scroll to zoom
            </div>
            <TransformComponent
              wrapperStyle={{ width: '100%', height: '100%', overflow: 'hidden', touchAction: 'none', cursor: 'grab' }}
              contentStyle={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <div className="relative inline-block select-none" style={{ cursor: 'grab' }}>
                <img
                  src={imageUrl}
                  alt="Floor plan"
                  className="max-w-none block pointer-events-none object-contain"
                  draggable={false}
                  style={{ maxWidth: dimensions.width, maxHeight: dimensions.height }}
                  onError={onImageError}
                />
                {floorplanHotspots.map((h) => {
                  const isHovered = h.id === hoveredId;
                  return (
                    <button
                      key={h.id}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onHotspotClick(h); }}
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        onHotspotHover?.(h, { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
                      }}
                      onMouseLeave={() => onHotspotHover?.(null, null)}
                      className={`planos-hotspot absolute w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-200 hover:scale-125 hover:shadow-lg border-white cursor-pointer overflow-hidden ${isHovered ? 'animate-hotspot-pulse' : ''}`}
                      style={{
                        left: `${h.x_pct!}%`,
                        top: `${h.y_pct!}%`,
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: h.icon_color ?? primaryColor,
                        opacity: h.id === selectedId ? 1 : 0.9,
                        boxShadow: isHovered ? `0 0 0 4px ${h.icon_color ?? primaryColor}, 0 0 20px ${(h.icon_color ?? primaryColor)}99` : h.id === selectedId ? `0 0 0 3px ${(h.icon_color ?? primaryColor)}80` : undefined,
                      }}
                    >
                      <HotspotIconComponent type={h.icon_type} iconUrl={h.icon_url} />
                    </button>
                  );
                })}
              </div>
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    </div>
  );
};

export default ZoomableFloorPlan;
